import { useOrg } from '@/contexts/OrgContext';

/**
 * Read the org context without requiring a `TeamProvider`.
 *
 * Returns the same value as `useOrg()` when rendered inside a provider, and
 * `undefined` when rendered outside one — instead of throwing. Use this from
 * hooks/components that treat the current org as an optional fallback (e.g.
 * `useFeatureFlag` with an explicit `orgId` option, or an embedded badge
 * preview rendered outside the app shell) and must fail closed rather than
 * crash when no provider is mounted.
 *
 * Deliberately implemented as a try/catch around `useOrg()` rather than a
 * direct `useContext(OrgContext)` read:
 * - `useOrg()` is `useContext` + an explicit throw when the value is
 *   `undefined`; the hook itself is still called unconditionally on every
 *   render, so the Rules of Hooks hold. `useContext` never throws — the only
 *   thing caught here is the wrapper's own "outside a TeamProvider" error.
 * - The entire test suite mocks `@/contexts/OrgContext` per-file with
 *   factories that define `useOrg` only (the documented `mockOrgContext()`
 *   pattern). Importing the raw `OrgContext` object from that module makes
 *   vitest throw `No "OrgContext" export is defined on the mock` in every
 *   such file whose tree evaluates a feature flag. Routing through `useOrg`
 *   keeps all of those mocks working unchanged.
 */
export function useOptionalOrg(): ReturnType<typeof useOrg> | undefined {
  try {
    return useOrg();
  } catch {
    return undefined;
  }
}
